// Mirrors the backend guard (companiesapi.create_with_dreamer.is_valid_domain).
// Quantifiers stay bounded ({1,63}) — an unbounded `+` would invite catastrophic backtracking.
const DOMAIN_PATTERN =
  /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/;

// Matches the DNS hostname limit the backend enforces via its (?=.{1,253}$) lookahead.
const MAX_DOMAIN_LENGTH = 253;

// Deliberately short — the backend's FREE_EMAIL_DOMAINS is authoritative and catches what this misses.
const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "proton.me",
  "protonmail.com"
]);

/** Strip scheme, userinfo, `www.`, port, path, query and fragment from a user-entered website. */
export const extractDomain = (value: string): string | null => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  // Isolate the authority like a URL parser would: cut at /?#, then drop userinfo and port.
  const authority = trimmed.replace(/^https?:\/\//, "").split(/[/?#]/)[0];
  const host = authority
    .slice(authority.lastIndexOf("@") + 1)
    .split(":")[0]
    .replace(/^www\./, "");
  return host || null;
};

/** True when the domain is a personal mailbox provider rather than a company site. */
export const isPersonalEmailDomain = (value: string): boolean => {
  const domain = extractDomain(value);
  return !!domain && PERSONAL_EMAIL_DOMAINS.has(domain);
};

/** True when the value is a bare, lowercase domain name such as `acme.com`. */
export const isValidDomain = (domain: string): boolean =>
  domain.length <= MAX_DOMAIN_LENGTH && DOMAIN_PATTERN.test(domain);

export const isValidWebsite = (value: string): boolean => {
  const domain = extractDomain(value);
  if (!domain || PERSONAL_EMAIL_DOMAINS.has(domain)) return false;
  return isValidDomain(domain);
};
